<template>
  <div class="telegram-dashboard">
    <!-- Header da página -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">
            <i class="fa fa-telegram text-primary"></i>
            Painel de Monitoramento - Telegram
          </h1>
          <p class="page-description">
            Monitore o status dos crons e visualize alertas do sistema de notificações Telegram
          </p>
        </div>
        <div class="header-right">
          <Button
            @handle:click="atualizarDados"
            text="Atualizar"
            color="primary"
            class="btn-atualizar"
            :disabled="carregando"
            data-testid="btn-atualizar-painel">
            <i class="fa fa-refresh" :class="{ 'fa-spin': carregando }"></i>
          </Button>
        </div>
      </div>
    </div>

    <!-- Indicador de carregamento -->
    <div v-if="carregando && !painelCarregado" class="loading-container">
      <div class="loading-spinner">
        <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
        <p class="loading-text">Carregando dados do painel...</p>
      </div>
    </div>

    <!-- Painel de Alertas -->
    <div v-else class="dashboard-content">
      <div class="dashboard-card">
        <PainelAlertas 
          ref="painelAlertas"
          @loading="onPainelLoading"
          @loaded="onPainelLoaded"
          @error="onPainelError"
        />
      </div>
    </div>

    <!-- Estado de erro -->
    <div v-if="erro" class="error-container">
      <div class="error-content">
        <i class="fa fa-exclamation-triangle fa-3x text-danger"></i>
        <h3>Erro ao carregar painel</h3>
        <p>{{ erro }}</p>
        <Button
          @handle:click="recarregarPainel"
          text="Tentar novamente"
          color="primary"
          class="btn-retry">
          <i class="fa fa-refresh"></i>
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import PainelAlertas from '@/components/monitoria/configuracoes/PainelAlertas.vue'
import Button from '@/components/core/Button.vue'
import { useAuth } from '@/composables/useAuth'

export default {
  name: 'TelegramDashboard',
  components: {
    PainelAlertas,
    Button
  },
  data() {
    return {
      carregando: false,
      painelCarregado: false,
      erro: null,
      ultimaAtualizacao: null,
      auth: useAuth()
    }
  },
  computed: {
    usuarioAutorizado() {
      return this.auth.canAccessTelegram
    }
  },
  mounted() {
    this.verificarPermissoes();
  },
  methods: {
    verificarPermissoes() {
      if (!this.usuarioAutorizado) {
        this.$toasted.show('Acesso negado. Apenas usuários de monitoramento podem acessar esta página.', {
          position: 'top-right',
          type: 'error',
          duration: 5000
        });
        
        // Redireciona para dashboard ou página anterior
        this.$router.push('/dashboard');
        return;
      }
      
      // Se autorizado, carrega o painel
      this.carregarPainel();
    },
    
    async carregarPainel() {
      this.carregando = true;
      this.erro = null;
      
      try {
        // Aguarda um momento para mostrar o loading
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // O PainelAlertas carrega seus próprios dados
        this.painelCarregado = true;
        this.ultimaAtualizacao = new Date();
        
      } catch (error) {
        this.erro = 'Erro ao inicializar o painel de monitoramento';
        console.error('Erro ao carregar painel Telegram:', error);
      } finally {
        this.carregando = false;
      }
    },
    
    async atualizarDados() {
      if (this.carregando) return;
      
      this.carregando = true;
      this.erro = null;
      
      try {
        // Força atualização do componente PainelAlertas
        if (this.$refs.painelAlertas && this.$refs.painelAlertas.atualizarDados) {
          await this.$refs.painelAlertas.atualizarDados();
        } else {
          throw new Error('Componente PainelAlertas não está disponível');
        }
        
        this.ultimaAtualizacao = new Date();
        
        this.$toasted.show('🔄 Dados atualizados com sucesso!', {
          position: 'top-right',
          type: 'success',
          duration: 3000
        });
        
      } catch (error) {
        console.error('Erro ao atualizar dados:', error);
        this.erro = 'Erro ao atualizar dados do painel';
        
        this.$toasted.show('❌ Erro ao atualizar dados', {
          position: 'top-right',
          type: 'error',
          duration: 4000
        });
      } finally {
        this.carregando = false;
      }
    },
    
    recarregarPainel() {
      this.painelCarregado = false;
      this.carregarPainel();
    },
    
    onPainelLoading(loading) {
      this.carregando = loading;
    },
    
    onPainelLoaded() {
      this.painelCarregado = true;
      this.erro = null;
      this.ultimaAtualizacao = new Date();
    },
    
    onPainelError(error) {
      this.erro = error;
      this.painelCarregado = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.telegram-dashboard {
  min-height: 100vh;
  background: #f8f9fa;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: visible !important;
  /* Não criar contexto de empilhamento */
  transform: none !important;
  will-change: auto !important;
  contain: none !important;
  isolation: auto !important;
}

.dashboard-header {
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 20px 24px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  flex-shrink: 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 100%;
  margin: 0;
}

.header-left {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title i {
  font-size: 1.8rem;
}

.page-description {
  color: #6c757d;
  margin: 0;
  font-size: 1rem;
  line-height: 1.4;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-atualizar {
  padding: 12px 24px;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.btn-atualizar:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.dashboard-content {
  flex: 1;
  padding: 24px;
  overflow: visible !important;
  display: flex;
  flex-direction: column;
  /* Não criar contexto de empilhamento */
  transform: none !important;
  will-change: auto !important;
  contain: none !important;
  isolation: auto !important;
}

.dashboard-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  border: 1px solid #e9ecef;
  flex: 1;
  overflow: visible !important;
  display: flex;
  flex-direction: column;
  /* Não criar contexto de empilhamento */
  transform: none !important;
  will-change: auto !important;
  contain: none !important;
  isolation: auto !important;
}

.loading-container, .error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 32px;
}

.loading-spinner, .error-content {
  text-align: center;
}

.loading-text {
  margin-top: 16px;
  color: #6c757d;
  font-size: 1.1rem;
}

.error-content h3 {
  color: #dc3545;
  margin: 16px 0 8px 0;
}

.error-content p {
  color: #6c757d;
  margin-bottom: 24px;
}

.btn-retry {
  padding: 12px 24px;
  font-weight: 500;
}

/* Responsividade */
@media (max-width: 992px) {
  .dashboard-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .header-right {
    width: 100%;
    justify-content: flex-end;
  }
  
  .page-title {
    font-size: 1.6rem;
  }
  
  .dashboard-content {
    padding: 16px;
  }
}

@media (max-width: 576px) {
  .dashboard-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 1.4rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  
  .page-description {
    font-size: 0.85rem;
  }
  
  .dashboard-content {
    padding: 12px;
  }
  
  .btn-atualizar {
    width: 100%;
    justify-content: center;
    font-size: 0.9rem;
  }
}

/* Animações - usando apenas opacity para não criar contexto de empilhamento */
.dashboard-card {
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.loading-spinner {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
